/**
 * Will this photo print sharply in this slot?
 *
 * The customer's photo is placed at a physical size on paper. Divide its pixel
 * count by that printed size and you get the real print resolution — the only
 * number that decides whether a print looks sharp or soft. No app setting can
 * raise it: a photo that arrived small (WhatsApp re-compresses to ~1600 px, and
 * "Photo" mode in most chat apps does the same) simply has fewer pixels than
 * the page needs.
 *
 * This module exists so the editor can say so BEFORE the album is printed,
 * while the customer can still re-send the original. It is advisory only — it
 * never blocks, never changes layout, and never touches draw geometry.
 *
 * Slot fractions mirror getSlots() in pdfGenerator.ts. They are FRACTIONS of
 * the page rather than absolute points so this module stays independent of the
 * page-size constants (and of cover geometry, which is a different spec).
 */
import { PRINT_MAX_DIM } from './imageLimits';
import { slotFractions } from './photoGap';

/** Interior page trim, mm. Matches PAGE_W / A4_H in pdfGenerator.ts. */
export const PAGE_W_MM = 200;
export const PAGE_H_MM = 297;

const MM_PER_INCH = 25.4;

/** Photo-print standard. Below this a print starts to look soft. */
export const GOOD_DPI = 300;
/** Acceptable for a small slot / a photo the customer insists on. */
export const WARN_DPI = 200;
/** Visibly soft at any size. */
export const BAD_DPI = 150;

export type PrintQualityLevel = 'good' | 'warn' | 'bad';

export interface PrintQuality {
  /** Effective print resolution in dots per inch, rounded. */
  dpi: number;
  level: PrintQualityLevel;
  /** Long edge (px) this slot wants for a 300 dpi print. */
  neededPx: number;
}

/**
 * Printed size of one slot, as a fraction of the page. Index matches the slot
 * index used by getSlots() / slot_positions (`s0`, `s1`, …).
 */
// B5 — slot sizes come from the shared geometry, at the album's gap. The
// switch that used to live here was a fourth copy of the layout table.
function slotFraction(layout: string, slotIndex: number, topHFrac?: number, gapMm = 0): { wFrac: number; hFrac: number } {
  return slotFractions(layout, slotIndex, gapMm, topHFrac);
}

/**
 * Effective print resolution of a photo in a given slot.
 *
 * @param imgW  photo pixel width (0/unknown → null, we don't guess)
 * @param imgH  photo pixel height
 * @param layout the page layout id
 * @param slotIndex which slot on that page
 * @param userScale the customer's zoom (>1 magnifies, so fewer source pixels
 *   cover the slot and the effective dpi drops). Values below 1 are ignored.
 * @param topHFrac wide-top band share, when the layout is 'wide-top'
 * @returns null when dimensions are unknown — callers must show nothing rather
 *   than a made-up number.
 */
export function photoPrintQuality(
  imgW: number | null | undefined,
  imgH: number | null | undefined,
  layout: string,
  slotIndex: number,
  userScale = 1,
  topHFrac?: number,
  /** B5 — the album's gap, mm. A gap shrinks every slot, so the DPI changes. */
  gapMm = 0,
): PrintQuality | null {
  if (!imgW || !imgH || !Number.isFinite(imgW) || !Number.isFinite(imgH) || imgW <= 0 || imgH <= 0) {
    return null;
  }
  // photos.width/height record the SOURCE dimensions, but intake stores the
  // print original capped at PRINT_MAX_DIM. A 6000 px DSLR frame is kept at
  // 4800, so crediting the full 6000 would overstate the print resolution.
  // Clamp BOTH axes by the same factor so the aspect ratio is preserved.
  const clamp = Math.min(1, PRINT_MAX_DIM / Math.max(imgW, imgH));
  const px = { w: imgW * clamp, h: imgH * clamp };

  const { wFrac, hFrac } = slotFraction(layout, slotIndex, topHFrac, gapMm);
  const slotWmm = PAGE_W_MM * wFrac;
  const slotHmm = PAGE_H_MM * hFrac;

  // COVER-FIT, not long-edge-vs-long-edge. The photo is scaled up until it
  // FILLS the slot on both axes, and the overflow is cropped away — so the
  // binding axis is whichever needs the most magnification, and that is what
  // sets the printed resolution.
  //
  // Getting this wrong is easy and silent: a portrait 1200×1600 photo in a
  // landscape half-page slot scores 203 dpi by long edges but actually prints
  // at ~150, because filling the 200 mm width forces the height out to 267 mm.
  // Verified against pdfimages on order #1116, which measured 148 dpi.
  const zoom = Math.max(userScale, 1);
  const mmPerPxW = slotWmm / px.w;
  const mmPerPxH = slotHmm / px.h;
  const mmPerPx = Math.max(mmPerPxW, mmPerPxH) * zoom;

  const dpi = MM_PER_INCH / mmPerPx;

  // The slot dimension driving the magnification — the axis the customer would
  // have to send more pixels along to reach 300 dpi.
  const bindingSlotMm = mmPerPxW >= mmPerPxH ? slotWmm : slotHmm;

  const level: PrintQualityLevel = dpi >= WARN_DPI ? 'good' : dpi >= BAD_DPI ? 'warn' : 'bad';

  return {
    dpi: Math.round(dpi),
    level,
    neededPx: Math.round((bindingSlotMm * GOOD_DPI * zoom) / MM_PER_INCH),
  };
}
